import { Region } from '@/constants/regions';

export interface DatacenterConfig {
  thoughtspotUrls: Record<Region, string>;
  apiGatewayUrls: Record<Region, string>;
  unifiedPlatformApiUrls: Record<Region, string>;
  surveyUrls: Record<Region, string>;
  digitalUrls: Record<Region, string>;
  workflowUrls: Record<Region, string>;
  pulseUrls: Record<Region, string>;
  reputationUrls: Record<Region, string>;
  listingsUrls: Record<Region, string>;
  digitalMarketingUrls: Record<Region, string>;
  pulseMarketingUrls: Record<Region, string>;
  reputationMarketingUrls: Record<Region, string>;
  listingsMarketingUrls: Record<Region, string>;
  defaultDatacenter: Region;
}

/**
 * Parse JSON string configuration for regional URLs
 */
export function parseRegionConfig<T extends Record<Region, string>>(
  jsonString: string | undefined,
  defaultValue: T,
): T {
  if (!jsonString) return defaultValue;

  try {
    const parsed = JSON.parse(jsonString);
    return { ...defaultValue, ...parsed } as T;
  } catch (error) {
    console.error('Failed to parse region config:', error);
    return defaultValue;
  }
}

/**
 * Create an empty region configuration object with all regions mapped to empty strings
 */
export function createEmptyRegionConfig(): Record<Region, string> {
  return {
    us: '',
    eu: '',
    ca: '',
    au: '',
    'shared-dev': '',
    staging6: '',
    staging8: '',
  };
}

/**
 * Load datacenter configuration from environment variables
 */
export function loadDatacenterConfig(): DatacenterConfig {
  const defaultDatacenter = (import.meta.env.VITE_DEFAULT_REGION as Region) || 'shared-dev';

  const parseUrlConfig = (envVar: string) =>
    parseRegionConfig<Record<Region, string>>(envVar, createEmptyRegionConfig());

  return {
    thoughtspotUrls: parseUrlConfig(import.meta.env.VITE_THOUGHTSPOT_URLS),
    apiGatewayUrls: parseUrlConfig(import.meta.env.VITE_API_GATEWAY_URLS),
    unifiedPlatformApiUrls: parseUrlConfig(import.meta.env.VITE_UNIFIED_PLATFORM_API_BASE_URL),
    surveyUrls: parseUrlConfig(import.meta.env.VITE_UNINAV_SURVEY_URLS),
    digitalUrls: parseUrlConfig(import.meta.env.VITE_UNINAV_DIGITAL_URLS),
    workflowUrls: parseUrlConfig(import.meta.env.VITE_UNINAV_WORKFLOW_URLS),
    pulseUrls: parseUrlConfig(import.meta.env.VITE_UNINAV_PULSE_URLS),
    reputationUrls: parseUrlConfig(import.meta.env.VITE_UNINAV_REPUTATION_URLS),
    listingsUrls: parseUrlConfig(import.meta.env.VITE_UNINAV_LISTINGS_URLS),
    digitalMarketingUrls: parseUrlConfig(import.meta.env.VITE_UNINAV_DIGITAL_MARKETING_URLS),
    pulseMarketingUrls: parseUrlConfig(import.meta.env.VITE_UNINAV_PULSE_MARKETING_URLS),
    reputationMarketingUrls: parseUrlConfig(import.meta.env.VITE_UNINAV_REPUTATION_MARKETING_URLS),
    listingsMarketingUrls: parseUrlConfig(import.meta.env.VITE_UNINAV_LISTINGS_MARKETING_URLS),
    defaultDatacenter,
  };
}
